import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Loader2, Plus, Search, X, Inbox, CheckCircle2 } from 'lucide-react';
import { useActiveClinic } from '@/lib/activeClinic';
import {
  useReferralsList,
  type ReferralAssignmentFilter,
  type ReferralListFilters,
  type ReferralListItem,
  type ReferralStatus,
} from '@/hooks/useReferrals';
import { useClinicMembers } from '@/hooks/useClinicMembers';
import { Button } from '@/components/ui/Button';
import { ErrorAlert } from '@/components/ui/ErrorAlert';

const STATUSES: Array<ReferralStatus | 'all'> = ['all', 'new', 'accepted', 'declined'];
const ASSIGNMENTS: ReferralAssignmentFilter[] = ['all', 'unassigned', 'assigned'];

export function Referrals() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { activeClinicId } = useActiveClinic();

  const [statusFilter, setStatusFilter] = useState<ReferralStatus | 'all'>('all');
  const [assignmentFilter, setAssignmentFilter] = useState<ReferralAssignmentFilter>('all');
  const [searchTerm, setSearchTerm] = useState('');

  const filters = useMemo<ReferralListFilters>(
    () => ({
      status: statusFilter,
      assignment: assignmentFilter,
      search: searchTerm.trim() || undefined,
    }),
    [statusFilter, assignmentFilter, searchTerm],
  );

  const { data, isLoading, error } = useReferralsList(activeClinicId, filters);
  const { data: members } = useClinicMembers(activeClinicId);
  const referrals = data ?? [];

  return (
    <div className="min-h-screen bg-page-light dark:bg-page-dark">
      <main className="py-6">
        <div className="max-w-none px-6">
          <div className="md:flex md:items-center md:justify-between">
            <div className="min-w-0 flex-1">
              <h2 className="text-2xl font-bold leading-7 text-gray-900 dark:text-white sm:text-3xl sm:tracking-tight">
                {t('referrals.title')}
              </h2>
              <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
                {t('referrals.subtitle')}
              </p>
            </div>
            <div className="mt-4 flex md:ml-4 md:mt-0">
              <Button onClick={() => navigate('/henvisninger/new')} disabled={!activeClinicId}>
                <Plus className="mr-1 h-4 w-4" />
                {t('referrals.new_referral')}
              </Button>
            </div>
          </div>

          <div className="mt-6 flex flex-wrap items-center gap-3">
            <div className="relative max-w-md flex-1">
              <div className="pointer-events-none absolute inset-y-0 left-0 flex items-center pl-3">
                <Search className="h-5 w-5 text-gray-400" />
              </div>
              <input
                type="text"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                placeholder={t('referrals.search_placeholder')}
                className="block w-full rounded-md border border-gray-300 bg-white py-2 pl-10 pr-3 text-sm leading-5 placeholder-gray-500 focus:border-indigo-500 focus:placeholder-gray-400 focus:outline-none focus:ring-1 focus:ring-indigo-500 dark:border-gray-600 dark:bg-gray-700 dark:text-white dark:placeholder-gray-400"
              />
              {searchTerm && (
                <button
                  type="button"
                  onClick={() => setSearchTerm('')}
                  className="absolute inset-y-0 right-0 flex items-center pr-3 text-gray-400 hover:text-gray-500"
                  aria-label={t('referrals.clear_search')}
                >
                  <X className="h-5 w-5" />
                </button>
              )}
            </div>

            <div className="inline-flex rounded-md border border-gray-300 bg-white shadow-sm dark:border-gray-600 dark:bg-gray-800">
              {STATUSES.map((s) => (
                <button
                  key={s}
                  type="button"
                  onClick={() => setStatusFilter(s)}
                  className={`px-3 py-1.5 text-sm first:rounded-l-md last:rounded-r-md ${
                    statusFilter === s
                      ? 'bg-indigo-600 text-white'
                      : 'text-gray-700 hover:bg-gray-50 dark:text-gray-200 dark:hover:bg-gray-700'
                  }`}
                >
                  {t(`referrals.status_${s}`)}
                </button>
              ))}
            </div>

            <div className="inline-flex rounded-md border border-gray-300 bg-white shadow-sm dark:border-gray-600 dark:bg-gray-800">
              {ASSIGNMENTS.map((a) => (
                <button
                  key={a}
                  type="button"
                  onClick={() => setAssignmentFilter(a)}
                  className={`px-3 py-1.5 text-sm first:rounded-l-md last:rounded-r-md ${
                    assignmentFilter === a
                      ? 'bg-indigo-600 text-white'
                      : 'text-gray-700 hover:bg-gray-50 dark:text-gray-200 dark:hover:bg-gray-700'
                  }`}
                >
                  {t(`referrals.assignment_${a}`)}
                </button>
              ))}
            </div>
          </div>

          {error && (
            <div className="mt-6">
              <ErrorAlert message={t('referrals.load_error')} />
            </div>
          )}

          {isLoading ? (
            <div className="flex items-center justify-center py-16">
              <Loader2 className="h-6 w-6 animate-spin text-gray-400" />
            </div>
          ) : referrals.length === 0 ? (
            <EmptyState hasSearch={Boolean(searchTerm)} />
          ) : (
            <div className="mt-6 overflow-hidden sm:rounded-md">
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-300 dark:divide-gray-700">
                  <thead className="bg-gray-50 dark:bg-gray-800">
                    <tr>
                      <Th>{t('referrals.col_name')}</Th>
                      <Th>{t('referrals.col_cpr')}</Th>
                      <Th>{t('referrals.col_referrer')}</Th>
                      <Th>{t('referrals.col_received')}</Th>
                      <Th>{t('referrals.col_status')}</Th>
                      <Th>{t('referrals.col_info_progress')}</Th>
                      <Th>{t('referrals.col_assigned')}</Th>
                      <th scope="col" className="relative py-3.5 pl-3 pr-4 sm:pr-6">
                        <span className="sr-only">{t('referrals.col_actions')}</span>
                      </th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200 bg-white dark:divide-gray-700 dark:bg-card-dark">
                    {referrals.map((r) => (
                      <tr
                        key={r.id}
                        onClick={() => navigate(`/henvisninger/${r.id}`)}
                        className="cursor-pointer transition-colors duration-150 hover:bg-gray-50 dark:hover:bg-gray-800"
                      >
                        <td className="whitespace-nowrap py-4 pl-4 pr-3 text-sm sm:pl-6">
                          <div className="font-medium text-gray-900 dark:text-white">
                            {r.first_name} {r.last_name}
                          </div>
                        </td>
                        <td className="whitespace-nowrap px-3 py-4 text-sm">
                          {r.has_cpr ? (
                            <span className="inline-flex items-center rounded-full bg-emerald-50 px-2 py-0.5 text-xs font-medium text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-300">
                              <CheckCircle2 className="mr-1 h-3 w-3" />
                              CPR
                            </span>
                          ) : (
                            <span className="text-gray-400 dark:text-gray-500">—</span>
                          )}
                        </td>
                        <td className="px-3 py-4 text-sm">
                          {r.referrer_name ? (
                            <div>
                              <div className="font-medium text-gray-900 dark:text-white">
                                {r.referrer_name}
                              </div>
                              {r.referrer_type && (
                                <div className="text-xs text-gray-500 dark:text-gray-400">
                                  {t(`referrals.referrer_type_${r.referrer_type}`)}
                                </div>
                              )}
                            </div>
                          ) : (
                            <span className="text-gray-400 dark:text-gray-500">—</span>
                          )}
                        </td>
                        <td className="whitespace-nowrap px-3 py-4 text-sm text-gray-500 dark:text-gray-400">
                          {new Date(r.received_at).toLocaleDateString('da-DK')}
                        </td>
                        <td className="whitespace-nowrap px-3 py-4 text-sm">
                          <ReferralStatusBadge status={r.status} />
                        </td>
                        <td className="whitespace-nowrap px-3 py-4 text-sm">
                          <InfoCollectionProgress referral={r} />
                        </td>
                        <td className="px-3 py-4 text-sm text-gray-500 dark:text-gray-400">
                          {r.assigned_clinician_id
                            ? members?.find((m) => m.user_id === r.assigned_clinician_id)?.name ??
                              t('referrals.assigned_unknown')
                            : '—'}
                        </td>
                        <td className="relative whitespace-nowrap py-4 pl-3 pr-4 text-right text-sm font-medium sm:pr-6">
                          <button
                            type="button"
                            onClick={(e) => {
                              e.stopPropagation();
                              navigate(`/henvisninger/${r.id}`);
                            }}
                            className="text-indigo-600 hover:text-indigo-900 dark:text-indigo-400 dark:hover:text-indigo-300"
                          >
                            {t('referrals.see_details')}
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </div>
      </main>
    </div>
  );
}

function Th({ children }: { children: React.ReactNode }) {
  return (
    <th
      scope="col"
      className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900 first:pl-4 dark:text-white first:sm:pl-6"
    >
      {children}
    </th>
  );
}

export function ReferralStatusBadge({ status }: { status: ReferralListItem['status'] }) {
  const { t } = useTranslation();
  const cls: Record<ReferralStatus, string> = {
    new: 'bg-blue-50 text-blue-700 dark:bg-blue-900/30 dark:text-blue-300',
    accepted: 'bg-emerald-50 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-300',
    declined: 'bg-red-50 text-red-700 dark:bg-red-900/30 dark:text-red-300',
  };
  return (
    <span
      className={`inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium ${cls[status]}`}
    >
      {t(`referrals.status_${status}`)}
    </span>
  );
}

function InfoCollectionProgress({ referral }: { referral: ReferralListItem }) {
  const fields = [
    referral.consent_collected,
    referral.info_letter_sent,
    referral.agreement_made,
    referral.payment_arranged,
  ];
  
  const collected = fields.filter(Boolean).length;
  const total = 4;

  return (
    <div className="flex items-center gap-1.5">
      <span className="text-xs font-medium text-gray-600 dark:text-gray-400">
        {collected}/{total}
      </span>
      <div className="flex gap-0.5">
        {fields.map((isChecked, i) => (
          <div
            key={i}
            className={`h-1.5 w-1.5 rounded-full ${
              isChecked
                ? 'bg-emerald-500 dark:bg-emerald-400'
                : 'bg-gray-300 dark:bg-gray-600'
            }`}
          />
        ))}
      </div>
    </div>
  );
}

function EmptyState({ hasSearch }: { hasSearch: boolean }) {
  const { t } = useTranslation();
  return (
    <div className="mt-12 text-center">
      <Inbox className="mx-auto h-10 w-10 text-gray-400" />
      <h3 className="mt-4 text-base font-medium text-gray-900 dark:text-white">
        {hasSearch ? t('referrals.empty_search_title') : t('referrals.empty_title')}
      </h3>
      <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
        {hasSearch ? t('referrals.empty_search_subtitle') : t('referrals.empty_subtitle')}
      </p>
    </div>
  );
}
